import { supabase } from "@/lib/supabase";
import type { WellnessScore, InsertWellnessScore } from "@shared/schema";

export const WELLNESS_DOMAINS = [
  { key: "physical", label: "Physical", icon: "💪", color: "#ef4444", description: "Body health, exercise, sleep, nutrition" },
  { key: "emotional", label: "Emotional", icon: "❤️", color: "#f97316", description: "Feelings, mood, emotional regulation" },
  { key: "spiritual", label: "Spiritual", icon: "✨", color: "#eab308", description: "Purpose, meaning, connection to something greater" },
  { key: "mental", label: "Mental", icon: "🧠", color: "#22c55e", description: "Cognitive health, learning, mental clarity" },
  { key: "relational", label: "Relational", icon: "🤝", color: "#06b6d4", description: "Relationships, community, social connections" },
  { key: "financial", label: "Financial", icon: "💰", color: "#3b82f6", description: "Money management, security, abundance" },
  { key: "vocational", label: "Vocational", icon: "💼", color: "#8b5cf6", description: "Work, career, professional growth" },
] as const;

export type WellnessDomain = typeof WELLNESS_DOMAINS[number]["key"];

export const wellnessService = {
  async getTodayScore(userId: string): Promise<WellnessScore | null> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const { data, error } = await supabase
      .from("wellness_scores")
      .select("id, user_id, date, physical, emotional, spiritual, mental, relational, financial, vocational, who_am_i, notes, created_at, updated_at")
      .eq("user_id", userId)
      .gte("date", today.toISOString())
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("Error fetching today's wellness score:", error);
    }
    return data;
  },

  async getRecentScores(userId: string, days: number = 7): Promise<WellnessScore[]> {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    startDate.setHours(0, 0, 0, 0);

    const { data, error } = await supabase
      .from("wellness_scores")
      .select("id, user_id, date, physical, emotional, spiritual, mental, relational, financial, vocational, who_am_i, notes, created_at, updated_at")
      .eq("user_id", userId)
      .gte("date", startDate.toISOString())
      .order("date", { ascending: true });

    if (error) {
      console.error("Error fetching recent wellness scores:", error);
      return [];
    }
    return data || [];
  },

  async saveScore(score: InsertWellnessScore): Promise<WellnessScore | null> {
    const { data, error } = await supabase
      .from("wellness_scores")
      .upsert(score, { onConflict: "user_id,date" })
      .select()
      .single();

    if (error) {
      console.error("Error saving wellness score:", error);
      return null;
    }
    return data;
  },

  async updateScore(userId: string, updates: Partial<InsertWellnessScore>): Promise<WellnessScore | null> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const existingScore = await this.getTodayScore(userId);
    
    if (existingScore) {
      const { data, error } = await supabase
        .from("wellness_scores")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", existingScore.id)
        .select()
        .single();

      if (error) {
        console.error("Error updating wellness score:", error);
        return null;
      }
      return data;
    } else {
      return this.saveScore({
        userId,
        date: today,
        ...updates,
      } as InsertWellnessScore);
    }
  },

  calculateOverallScore(score: WellnessScore): number {
    const domains = ["physical", "emotional", "spiritual", "mental", "relational", "financial", "vocational"] as const;
    let total = 0;
    let count = 0;
    
    for (const domain of domains) {
      const value = parseInt(score[domain] || "5", 10);
      if (!isNaN(value)) {
        total += value;
        count++;
      }
    }
    
    return count > 0 ? Math.round(total / count * 10) / 10 : 5;
  },

  getScoreLabel(score: number): { label: string; color: string } {
    if (score >= 9) return { label: "Thriving", color: "#22c55e" };
    if (score >= 7) return { label: "Good", color: "#84cc16" };
    if (score >= 5) return { label: "Stable", color: "#eab308" };
    if (score >= 3) return { label: "Struggling", color: "#f97316" };
    return { label: "Critical", color: "#ef4444" };
  },
};
